import { SnapshotScenarioDetailsInput } from 'src/API';
import { createSnapshot, refreshSnapshot } from 'src/graphql/mutations';
import { listSnapshotScenarios } from '../../graphql/queries';
import { ScenarioSnapshot } from '../../types/snapshots/snapshot.type';
import { formatApiError } from '../../utils/errorUtils';
import { logger } from '../../utils/logger';
import { fetchGraphQLRequest } from './clients/graphqlClient';

// ============================================================================
// Scenario Snapshot Operations
// ============================================================================

/**
 * Fetches scenario snapshots from the API
 * @param params - Optional filter parameters
 * @returns Promise resolving to array of scenario snapshots (using future ScenarioSnapshot interface)
 * @throws Error if API call fails
 */
export const fetchScenarioSnapshotsAPI = async (scenario_id?: number): Promise<ScenarioSnapshot[]> => {
  try {
    logger.info('Fetching scenario snapshots from API', { scenario_id: scenario_id || '-' });

    const result = await fetchGraphQLRequest<{ listSnapshotScenarios: ScenarioSnapshot[] }>({
      query: listSnapshotScenarios,
      variables: scenario_id ? { scenario_id: scenario_id } : {},
      requestName: 'listSnapshotScenarios',
      requestType: 'query',
    });

    const scenarioSnapshots = result.listSnapshotScenarios || [];
    logger.info(`Fetched ${scenarioSnapshots.length} Scenario Snapshots from API`);
    return scenarioSnapshots;
  } catch (error) {
    logger.error('Error fetching scenario snapshots from API:', { error });
    throw formatApiError('Failed to fetch scenario snapshots', error);
  }
};

/**
 * Creates scenario snapshot from the API
 * @param mutationData
 * @returns Promise resolving to api response object
 * @throws Error if API call fails
 */

export const createScenarioSnapshotAPI = async (
  mutationData: SnapshotScenarioDetailsInput
): Promise<{ success: boolean; message: string }> => {
  try {
    logger.info(`Creating snapshot to GraphQL API:`, mutationData);
    const result = await fetchGraphQLRequest<{ createSnapshot: { numberOfRecordsUpdated: number } }>({
      query: createSnapshot,
      variables: { input: mutationData },
      requestName: 'createSnapshot',
      requestType: 'mutation',
      auditConfig: { pageName: 'create_snapshot' },
    });

    const numberOfRecordsUpdated = result.createSnapshot.numberOfRecordsUpdated || 0;
    logger.info(`Snapshot created successfully`, { numberOfRecordsUpdated });
    return {
      success: numberOfRecordsUpdated > 0,
      message: `Snapshot created successfully (${numberOfRecordsUpdated} records updated)`,
    };
  } catch (error) {
    logger.error('Error creating scenario snapshot from API:', { error });
    throw formatApiError('Failed to create scenario snapshot', error);
  }
};

/**
 * Refreshes scenario snapshot from the API
 * @param mutationData
 * @returns Promise resolving to api response object
 * @throws Error if API call fails
 */

export const refreshScenarioSnapshotAPI = async (
  mutationData: SnapshotScenarioDetailsInput
): Promise<{ success: boolean; message: string }> => {
  try {
    logger.info(`Refreshing snapshot to GraphQL API:`, mutationData);
    const result = await fetchGraphQLRequest<{ refreshSnapshot: { numberOfRecordsUpdated: number } }>({
      query: refreshSnapshot,
      variables: { input: mutationData },
      requestName: 'refreshSnapshot',
      requestType: 'mutation',
      auditConfig: { pageName: 'refresh_snapshot' },
    });

    const numberOfRecordsUpdated = result.refreshSnapshot.numberOfRecordsUpdated || 0;
    logger.info(`Snapshot refreshed successfully`, { numberOfRecordsUpdated });
    return {
      success: numberOfRecordsUpdated > 0,
      message: `Snapshot refreshed successfully (${numberOfRecordsUpdated} records updated)`,
    };
  } catch (error) {
    logger.error('Error refreshing scenario snapshot from API:', { error });
    throw formatApiError('Failed to refresh scenario snapshot', error);
  }
};
